import React from 'react';
import { IoAlertCircleOutline, IoCloseOutline, IoPersonOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import PointContainer from './PointContainer';
import OptionModal from '../layouts/OptionModal';
import { ConfirmModal } from '../layouts/ConfirmModal';
import { AlertModal } from '../layouts/AlertModal';
import { block as blockMember } from '../../api/BlockApi';

const ChatSideMenu = ({ isOpen, onClose, chatRoomInfo, meetingId, onBlocked }) => {
  const navigate = useNavigate();
  const member = useAuthStore(state => state.member);

  const [actionTarget, setActionTarget] = React.useState({ id: null, name: '' });
  const [isOptionOpen, setIsOptionOpen] = React.useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [alertState, setAlertState] = React.useState({ isVisible: false, message: '' });

  if (!isOpen) return null;

  const openActionMenu = (targetId, targetName) => {
    setActionTarget({ id: targetId, name: targetName });
    setIsOptionOpen(true);
  };

  const handleReport = () => {
    if (!actionTarget.id) return;
    navigate(`/app/chat/${meetingId}/report/in-meeting`, {
      state: {
        reportedId: actionTarget.id,
        meetingId: meetingId
      }
    });
    setIsOptionOpen(false);
    onClose();
  };

  const handleAskBlock = () => {
    setIsOptionOpen(false);
    setIsConfirmOpen(true);
  };

  const handleConfirmBlock = async () => {
    try {
      if (!actionTarget.id) return;
      await blockMember(actionTarget.id);
      setAlertState({ isVisible: true, message: '해당 밥친구가 차단되었습니다.' });
    } catch (e) {
      setAlertState({ isVisible: true, message: e.response?.data?.message || '차단 처리 중 오류가 발생했습니다.' });
    } finally {
      setIsConfirmOpen(false);
    }
  };

  const handleAlertClose = () => {
    setAlertState({ isVisible: false, message: '' });
    if (onBlocked) {
      onBlocked();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* 사이드 메뉴 패널 */}
      <div className="absolute top-0 right-0 h-full w-2/3 bg-white shadow-xl">
        {/* 사이드 메뉴 헤더 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">참여자</h2>
          <button
            onClick={onClose}
            className="p-1 active:bg-gray-100 rounded-full"
          >
            <IoCloseOutline size={24} className="text-gray-700" />
          </button>
        </div>

        {/* 멤버 리스트 */}
        <div className="p-4">
          {chatRoomInfo?.members && (
            <div className="space-y-4">
              {/* 멤버들을 정렬: 호스트가 먼저, 나머지는 이름순 */}
              {[...chatRoomInfo.members]
                .sort((a, b) => {
                  if (a.id === chatRoomInfo.hostId) return -1;
                  if (b.id === chatRoomInfo.hostId) return 1;
                  return a.name.localeCompare(b.name);
                })
                .map((memberItem) => (
                  <div key={memberItem.id} className="flex items-center">
                    {/* 프로필 이미지 */}
                    <div className="mr-3 flex-shrink-0 relative">
                      {memberItem.imageUrl ? (
                        <img
                          src={memberItem.imageUrl}
                          alt={memberItem.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                          <IoPersonOutline size={24} className="text-gray-600" />
                        </div>
                      )}
                      {/* 포인트 표시 */}
                      {memberItem.point !== undefined && (
                        <div className="absolute -bottom-1 -right-1">
                          <PointContainer point={memberItem.point} size={32} />
                        </div>
                      )}
                    </div>

                    {/* 멤버 정보 */}
                    <div className="flex-1">
                      <div className="flex items-center justify_between">
                        <div className="flex items-center">
                          <span className="text-base font-medium text-gray-900">
                            {memberItem.name}
                          </span>
                          {memberItem.id === chatRoomInfo.hostId && (
                            <span className="ml-2 text-base">👑</span>
                          )}
                        </div>

                        {/* 신고/차단 옵션 열기 버튼 - 본인이 아닐 때만 표시 */}
                        {memberItem.id !== member?.id && (
                          <button
                            onClick={() => openActionMenu(memberItem.id, memberItem.name)}
                            className="p-2 text-red-500 active:bg-red-50 rounded-full transition-colors"
                          >
                            <IoAlertCircleOutline size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>

      {/* 옵션 모달 */}
      <OptionModal
        isOpen={isOptionOpen}
        onClose={() => setIsOptionOpen(false)}
        title={actionTarget.name ? `밥친구 ${actionTarget.name}에 대하여` : '해당 밥친구에 대하여'}
        options={[
          { label: '신고하기', onClick: handleReport, destructive: true },
          { label: '차단하기', onClick: handleAskBlock },
        ]}
      />

      {/* 차단 확인 모달 */}
      <ConfirmModal
        isVisible={isConfirmOpen}
        title="차단하기"
        message={'해당 밥친구를 차단하시겠습니까? 해당 밥친구의 모든 채팅과 모임이 보이지 않게 됩니다'}
        onConfirm={handleConfirmBlock}
        onCancel={() => setIsConfirmOpen(false)}
      />

      {/* 알림 모달 */}
      <AlertModal
        isVisible={alertState.isVisible}
        message={alertState.message}
        onClose={handleAlertClose}
        callback={null}
      />
    </div>
  );
};

export default ChatSideMenu;
